import dayjs, { type Dayjs } from "dayjs";
import { MONTH_NAMES, getHijriYear, toGregorianUtc } from "@/utils/hijri-date-utils";
import { formatListDate } from "@/utils/date-format";
import type { RaRecord } from "react-admin";

export type MiqaatRecord = RaRecord & {
  name?: string;
  mealType?: string;
  dateMode?: "PERMANENT" | "TEMPORARY" | string;
  hijriDay?: number;
  hijriMonth?: number;
  englishDate?: string | Date | null;
  venue?: string;
  venueId?: string;
  [key: string]: unknown;
};

export type MiqaatRelativeStatus = {
  label: string;
  color: "primary" | "success" | "warning" | "default";
  isUpcoming: boolean;
  isToday: boolean;
  isPast: boolean;
  daysDiff: number;
};

/**
 * Extracts a displayable string from any validation error (react-admin or react-hook-form error object).
 * Prevents "Objects are not valid as a React child" errors when error descriptors have nested { message: { message: '...' } }.
 */
export function getValidationErrorMessage(
  error: unknown,
  fallback = "Selection required"
): string | undefined {
  if (!error) return undefined;
  if (typeof error === "string") return error;
  if (typeof error === "object" && error !== null) {
    if ("message" in error) {
      const msg = (error as { message: unknown }).message;
      if (typeof msg === "string") {
        if (msg === "ra.validation.required") return "Required";
        return msg;
      }
      if (typeof msg === "object" && msg !== null) {
        return getValidationErrorMessage(msg, fallback);
      }
    }
  }
  return fallback;
}

/**
 * Resolves the calendar occurrence Date (Gregorian) for any Miqaat,
 * whether defined as TEMPORARY (Gregorian date) or PERMANENT (annual Hijri month/day).
 */
export function resolveMiqaatOccurrenceDate(
  miqaat?: MiqaatRecord | null,
  referenceDate: Dayjs = dayjs()
): Dayjs | null {
  if (!miqaat) return null;

  // 1. Temporary or explicit English date
  if (miqaat.englishDate) {
    const d = dayjs(miqaat.englishDate);
    if (d.isValid()) return d;
  }

  // 2. Permanent annual Hijri date
  if (
    typeof miqaat.hijriMonth === "number" &&
    typeof miqaat.hijriDay === "number" &&
    miqaat.hijriMonth >= 0 &&
    miqaat.hijriMonth < 12 &&
    miqaat.hijriDay >= 1 &&
    miqaat.hijriDay <= 30
  ) {
    try {
      const today = referenceDate.startOf("day");
      const currentHijriYear = getHijriYear(today.toDate());
      const gregCurrentYear = toGregorianUtc({
        year: currentHijriYear,
        month: miqaat.hijriMonth,
        day: miqaat.hijriDay,
      });
      const currentOcc = dayjs(gregCurrentYear).startOf("day");

      // If the current cycle date was more than 14 days ago, the next occurrence
      // belongs to the upcoming Hijri year (annual recurring event).
      const diffDays = currentOcc.diff(today, "day");
      if (diffDays < -14) {
        const gregNextYear = toGregorianUtc({
          year: currentHijriYear + 1,
          month: miqaat.hijriMonth,
          day: miqaat.hijriDay,
        });
        return dayjs(gregNextYear).startOf("day");
      }

      return currentOcc;
    } catch {
      return null;
    }
  }

  return null;
}

/** Formats the date display for a Miqaat (Hijri with resolved Gregorian, or Gregorian). */
export function formatMiqaatDate(
  miqaat?: MiqaatRecord | null,
  referenceDate: Dayjs = dayjs()
): string {
  if (!miqaat) return "";

  if (miqaat.dateMode === "PERMANENT") {
    const m = typeof miqaat.hijriMonth === "number" ? miqaat.hijriMonth : 0;
    const d = typeof miqaat.hijriDay === "number" ? miqaat.hijriDay : 0;
    const monthLabel = m >= 0 && m < 12 ? (MONTH_NAMES.short.en[m] ?? `Month ${m + 1}`) : "—";
    const hijriStr = `Hijri ${d} ${monthLabel}`;

    const resolved = resolveMiqaatOccurrenceDate(miqaat, referenceDate);
    if (resolved && resolved.isValid()) {
      return `${hijriStr} · ${formatListDate(resolved.toDate())}`;
    }
    return hijriStr;
  }

  if (miqaat.englishDate) {
    const str = formatListDate(miqaat.englishDate);
    if (str) return str;
  }

  return "Date TBD";
}

/** Formats the meal type into a human-friendly label. */
export function formatMiqaatMealType(mealType?: string | null): string {
  const m = String(mealType ?? "")
    .trim()
    .toUpperCase();
  if (m === "LUNCH") return "Lunch";
  if (m === "DINNER") return "Dinner";
  return m || "Miqaat";
}

/** Computes the relative time tag (Today, Tomorrow, In X days, Past) from the resolved occurrence date. */
export function getMiqaatRelativeStatus(
  miqaat?: MiqaatRecord | null,
  referenceDate: Dayjs = dayjs()
): MiqaatRelativeStatus | null {
  if (!miqaat) return null;

  const occurrence = resolveMiqaatOccurrenceDate(miqaat, referenceDate);
  if (!occurrence || !occurrence.isValid()) return null;

  const eventDate = occurrence.startOf("day");
  const today = referenceDate.startOf("day");
  const diff = eventDate.diff(today, "day");

  if (diff === 0) {
    return {
      label: "Today",
      color: "success",
      isUpcoming: true,
      isToday: true,
      isPast: false,
      daysDiff: 0,
    };
  }
  if (diff === 1) {
    return {
      label: "Tomorrow",
      color: "primary",
      isUpcoming: true,
      isToday: false,
      isPast: false,
      daysDiff: 1,
    };
  }
  if (diff > 1 && diff <= 7) {
    return {
      label: `In ${diff} days`,
      color: "primary",
      isUpcoming: true,
      isToday: false,
      isPast: false,
      daysDiff: diff,
    };
  }
  if (diff > 7 && diff <= 30) {
    return {
      label: `In ${Math.round(diff / 7)}w`,
      color: "default",
      isUpcoming: true,
      isToday: false,
      isPast: false,
      daysDiff: diff,
    };
  }
  if (diff > 30) {
    return {
      label: `In ${Math.round(diff / 30)}mo`,
      color: "default",
      isUpcoming: true,
      isToday: false,
      isPast: false,
      daysDiff: diff,
    };
  }

  return {
    label: "Past",
    color: "default",
    isUpcoming: false,
    isToday: false,
    isPast: true,
    daysDiff: diff,
  };
}

/**
 * Sorts Miqaat records so that:
 * 1. Imminent / upcoming events come first (sorted soonest to furthest)
 * 2. Events without explicit dates come next
 * 3. Past events come last (most recent past first)
 */
export function sortMiqaatsUpcomingFirst(
  miqaats: readonly MiqaatRecord[],
  referenceDate: Dayjs = dayjs()
): MiqaatRecord[] {
  const today = referenceDate.startOf("day");

  return [...miqaats].sort((a, b) => {
    const aOcc = resolveMiqaatOccurrenceDate(a, referenceDate);
    const bOcc = resolveMiqaatOccurrenceDate(b, referenceDate);

    const aValid = aOcc?.isValid() ?? false;
    const bValid = bOcc?.isValid() ?? false;

    const aDiff = aValid && aOcc ? aOcc.diff(today, "day") : null;
    const bDiff = bValid && bOcc ? bOcc.diff(today, "day") : null;

    const aIsUpcoming = aDiff !== null && aDiff >= 0;
    const bIsUpcoming = bDiff !== null && bDiff >= 0;
    const aIsPast = aDiff !== null && aDiff < 0;
    const bIsPast = bDiff !== null && bDiff < 0;

    // Upcoming first
    if (aIsUpcoming && !bIsUpcoming) return -1;
    if (!aIsUpcoming && bIsUpcoming) return 1;

    // Both upcoming: sort soonest first
    if (aIsUpcoming && bIsUpcoming && aDiff !== null && bDiff !== null) {
      if (aDiff !== bDiff) return aDiff - bDiff;
    }

    // Both undated: sort by name
    if (!aValid && !bValid) {
      return String(a.name ?? "").localeCompare(String(b.name ?? ""));
    }

    // Dated vs undated
    if (!aIsPast && !bIsPast) {
      if (aValid && !bValid) return -1;
      if (!aValid && bValid) return 1;
    }

    // Past events last (most recent first)
    if (aIsPast && bIsPast && aDiff !== null && bDiff !== null) {
      return bDiff - aDiff;
    }
    if (aIsPast && !bIsPast) return 1;
    if (!aIsPast && bIsPast) return -1;

    return String(a.name ?? "").localeCompare(String(b.name ?? ""));
  });
}

/** Finds the single soonest upcoming Miqaat. */
export function findSoonestUpcomingMiqaat(
  miqaats: readonly MiqaatRecord[],
  referenceDate: Dayjs = dayjs()
): MiqaatRecord | null {
  const today = referenceDate.startOf("day");
  const upcoming = miqaats.filter((m) => {
    const occ = resolveMiqaatOccurrenceDate(m, referenceDate);
    return occ?.isValid() && occ.diff(today, "day") >= 0;
  });

  if (upcoming.length === 0) return null;

  const sorted = [...upcoming].sort((a, b) => {
    const occA = resolveMiqaatOccurrenceDate(a, referenceDate)!;
    const occB = resolveMiqaatOccurrenceDate(b, referenceDate)!;
    return occA.diff(today, "day") - occB.diff(today, "day");
  });

  return sorted[0] ?? null;
}

/** Multi-attribute search filter for Miqaats. */
export function matchMiqaatSearch(
  miqaat: MiqaatRecord,
  query: string,
  referenceDate: Dayjs = dayjs()
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const name = String(miqaat.name ?? "").toLowerCase();
  const venue = String(miqaat.venue ?? "").toLowerCase();
  const mealType = formatMiqaatMealType(miqaat.mealType).toLowerCase();
  const dateStr = formatMiqaatDate(miqaat, referenceDate).toLowerCase();

  return name.includes(q) || venue.includes(q) || mealType.includes(q) || dateStr.includes(q);
}
